/**
 * <WelcomeBadge /> — glowing welcome pill rendered under the sidebar
 * logo on a newly-approved user's first session. The badge is
 * "stamped" at access-request approval time (Maker / Holding Admin
 * types a welcome message into the approve dialog) and lives on the
 * user record until dismissed.
 *
 * UX:
 *   - Tight pill with a subtle violet → fuchsia gradient + soft glow.
 *   - "Welcome to {brand} — from {admin_name}" preview text.
 *   - Click the badge → expands an inline card with the full message
 *     and a "Got it ✓" dismiss button. Dismissal persists via
 *     POST /api/auth/me/welcome/dismiss so the badge never returns.
 *
 * Renders nothing if the API returns notice=null (no welcome stamped
 * or already dismissed). Safe to mount unconditionally — it self-gates.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Sparkles, X, Heart } from 'lucide-react';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */

export default function WelcomeBadge({ compact = false }) {
  const [notice, setNotice] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/auth/me/welcome')
      .then(({ data }) => { if (alive && data?.notice) setNotice(data.notice); })
      .catch(() => { /* silent — badge just doesn't render */ });
    return () => { alive = false; };
  }, []);

  const dismiss = useCallback(async () => {
    setDismissing(true);
    try {
      await api.post('/auth/me/welcome/dismiss');
    } catch {
      /* swallow — UI dismisses regardless */
    } finally {
      setNotice(null);
    }
  }, []);

  if (!notice) return null;

  const fmtSentBy = notice.from_name || notice.from_email || 'your administrator';
  const brand = notice.brand_name || 'Vigilant Entities';

  if (compact) {
    // Pill-only variant — shows next to the logo, expands the full
    // card on click. Used for tight headers / mobile.
    return (
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="welcome-badge-compact"
        className="group relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all
                   bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white
                   shadow-[0_0_18px_-2px_rgba(174,82,234,0.55)] hover:shadow-[0_0_24px_-2px_rgba(232,121,249,0.7)]"
        title={`Welcome from ${fmtSentBy}`}
      >
        <Sparkles size={12} className="animate-pulse" />
        <span>Welcome</span>
      </button>
    );
  }

  return (
    <div
      data-testid="welcome-badge"
      className="relative mt-3 group"
    >
      {/* Outer glow halo — pure CSS, no JS animation cost */}
      <div
        aria-hidden
        className="absolute -inset-0.5 rounded-xl bg-gradient-to-br from-violet-500/40 via-fuchsia-500/40 to-amber-400/30 blur-md opacity-70 group-hover:opacity-100 transition-opacity"
      />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="welcome-badge-toggle"
        className="relative w-full text-left rounded-xl border border-violet-400/40
                   bg-gradient-to-br from-violet-600/95 via-fuchsia-600/90 to-violet-700/95
                   text-white px-3 py-2.5 shadow-lg
                   hover:from-violet-600 hover:via-fuchsia-500 hover:to-violet-700
                   transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="shrink-0 animate-pulse" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/85">
              Welcome to {brand}
            </div>
            <div className="text-[11px] text-white/95 truncate" title={notice.message}>
              {expanded ? `From ${fmtSentBy}` : notice.message}
            </div>
          </div>
          <Heart size={12} className="shrink-0 fill-white/40 stroke-white/85" />
        </div>
      </button>

      {expanded && (
        <div
          data-testid="welcome-badge-expanded"
          className="relative mt-2 rounded-xl border border-violet-400/40
                     bg-white/95 dark:bg-zinc-900/95 backdrop-blur
                     px-3 py-3 shadow-xl text-slate-800 dark:text-white/90"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700 dark:text-violet-300">
              Note from {fmtSentBy}
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-white"
              aria-label="Collapse"
            >
              <X size={12} />
            </button>
          </div>
          <p className="text-[12px] leading-relaxed whitespace-pre-line">
            {notice.message}
          </p>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={dismiss}
              disabled={dismissing}
              data-testid="welcome-badge-dismiss"
              className="px-3 py-1.5 rounded-md text-[11px] uppercase tracking-wider font-bold
                         bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-60"
            >
              {dismissing ? 'Saving…' : 'Got it ✓'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
